import axios from 'axios';
import * as Sentry from '@sentry/vue';
import { isAxiosNetworkOrTimeoutError, isAxiosRequestCanceled } from '@/utils/isAxiosRequestCanceled';

const baseURL = import.meta.env.VITE_APP_REST_API_V1;

const ABSOLUTE_HTTP_URL_REGEX = /^https?:\/\//i;

const HEADER_BLOCKLIST_FOR_EXTERNAL = [
  'authorization',
  'x-requested-with',
  'x-xsrf-token'
];

function getCookie(name) {
  if (typeof document === 'undefined') return null;

  const cookies = document.cookie
    ? document.cookie.split('; ')
    : [];

  const target = cookies.find((row) =>
    row.startsWith(`${name}=`)
  );

  if (!target) return null;

  return decodeURIComponent(
    target.split('=').slice(1).join('=')
  );
}

function isAbsoluteHttpUrl(url) {
  return typeof url === 'string'
    && ABSOLUTE_HTTP_URL_REGEX.test(url);
}

function getApiOrigin() {
  if (!isAbsoluteHttpUrl(baseURL)) return null;

  try {
    return new URL(baseURL).origin;
  } catch {
    return null;
  }
}

function getRequestOrigin(config) {
  if (!isAbsoluteHttpUrl(config?.url)) return null;

  try {
    return new URL(config.url).origin;
  } catch {
    return null;
  }
}

function removeBlockedHeadersForExternalRequest(headers) {
  if (!headers) return;

  const keys = Object.keys(headers);

  for (const key of keys) {

    const normalizedKey = String(key).toLowerCase();

    if (
      HEADER_BLOCKLIST_FOR_EXTERNAL.includes(normalizedKey)
      || normalizedKey.startsWith('x-amz-')
    ) {
      delete headers[key];
    }
  }
}

const api = axios.create({
  baseURL,

  withCredentials: true,

  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  }
});

// Sanctum
api.get('/sanctum/csrf-cookie').catch(() => {});

// REQUEST INTERCEPTOR
api.interceptors.request.use(
  (config) => {

    const requestOrigin = getRequestOrigin(config);
    const apiOrigin = getApiOrigin();

    // Segurança contra vazamento de credenciais
    if (
      requestOrigin
      && apiOrigin
      && requestOrigin !== apiOrigin
    ) {

      config.withCredentials = false;

      removeBlockedHeadersForExternalRequest(
        config.headers
      );

      return config;
    }

    // XSRF TOKEN
    const cookieToken = getCookie('XSRF-TOKEN');

    const legacyToken =
      localStorage.getItem('XSRF-TOKEN');

    const token = cookieToken || legacyToken;

    if (token) {
      config.headers['X-XSRF-TOKEN'] = token;
    }

    // AUTH TOKEN
    const authToken =
      localStorage.getItem('auth_token');

    if (authToken) {
      config.headers['Authorization']
        = `Bearer ${authToken}`;
    }

    /**
     * Instância define Content-Type: application/json. Com FormData, o transformRequest
     * do Axios 1.x faz JSON.stringify(formDataToJSON(data)) e o servidor recebe JSON em
     * vez de ficheiros — validação Laravel falha (ex.: _relativePath vazio).
     */
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      if (config.headers && typeof config.headers.delete === 'function') {
        config.headers.delete('Content-Type');
      }
    }

    return config;
  },

  (error) => {
    return Promise.reject(error);
  }
);

// RESPONSE INTERCEPTOR
api.interceptors.response.use(

  (response) => response,

  (error) => {

    // Evita erro sem response
    const status = error?.response?.status;

    // Ignora erros comuns
    const ignoredStatuses = [
      401,
      403,
      404,
      422
    ];

    // Abort/cancel (debounce, troca de rota) não deve ir ao Sentry
    if (isAxiosRequestCanceled(error)) {
      return Promise.reject(error);
    }

    // Timeout/rede: feedback na UI; não é exceção de aplicação
    if (isAxiosNetworkOrTimeoutError(error)) {
      return Promise.reject(error);
    }

    // Captura apenas erros relevantes
    if (!ignoredStatuses.includes(status)) {

      Sentry.captureException(error, {
        tags: {
          section: 'axios'
        },

        extra: {
          url: error?.config?.url,
          method: error?.config?.method,
          response: error?.response?.data
        }
      });
    }

    // 401
    if (status === 401) {
      console.warn('Unauthorized Request');
    }

    return Promise.reject(error);
  }
);

export default api;